import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiFileText
} from 'react-icons/fi';
import electronicPrescriptionService from '../../../../ElectronicPrescription/Service/ElectronicPrescriptionService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import ElectronicPrescriptionPDF from 'components/ElectronicPrescription/ElectronicPrescriptionPDF';

const ElectronicPrescriptionsTreatment = ({ onCountUpdate, componentName = 'ElectronicPrescriptionsTreatment' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [prescriptions, setPrescriptions] = useState([]);
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedPrescriptionForPDF, setSelectedPrescriptionForPDF] = useState(null);

  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: 'All',
    medicationType: '',
    prescribedBy: '',
    createdDate: '',
    endDate: '',
    expirationStatus: 'All'
  });

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
  const loadPrescriptions = useCallback(async () => {
    if (!patientId) {
      console.log('No patientId provided, component will show empty state');
      setPrescriptions([]);
      onCountUpdate && onCountUpdate(componentName, 0);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Loading prescriptions for patient:', patientId);
      const response = await electronicPrescriptionService.getElectronicPrescriptionsByPatient(patientId);
      console.log('Raw API response:', response);
      
      let prescriptionsData = [];
      
      if (response.data?.electronicPrescriptions) {
        prescriptionsData = Array.isArray(response.data.electronicPrescriptions) ? 
          response.data.electronicPrescriptions : [response.data.electronicPrescriptions];
      } else if (response.electronicPrescriptions) {
        prescriptionsData = Array.isArray(response.electronicPrescriptions) ? 
          response.electronicPrescriptions : [response.electronicPrescriptions];
      } else if (response.data && Array.isArray(response.data)) {
        prescriptionsData = response.data;
      } else if (Array.isArray(response)) {
        prescriptionsData = response;
      }

      const normalizedPrescriptions = prescriptionsData.map(prescription => {
        console.log('Processing prescription:', prescription);
        
        // Handle patient information (could be populated or just ID)
        const patientInfo = typeof prescription.patientInfo === 'object' && prescription.patientInfo 
          ? { 
              id: prescription.patientInfo.userId?._id || prescription.patientInfo.patientId, 
              name: prescription.patientInfo.fullName || 
                    `${prescription.patientInfo.userId?.name || ''} ${prescription.patientInfo.userId?.surname || ''}`.trim()
            }
          : { id: prescription.patient || patientId, name: patientData.patientName || 'Unknown Patient' };

        // Handle prescriber information
        const prescriberInfo = typeof prescription.prescriberInfo === 'object' && prescription.prescriberInfo
          ? { 
              id: prescription.prescriberInfo.userId?._id || prescription.prescriberInfo.doctorId,
              name: prescription.prescriberInfo.fullName || 
                    `${prescription.prescriberInfo.userId?.name || ''} ${prescription.prescriberInfo.userId?.surname || ''}`.trim(),
              collegiateNumber: prescription.prescriberInfo.collegiateNumber,
              specialty: prescription.prescriberInfo.specialty
            }
          : { id: prescription.prescriber, name: 'Unknown', collegiateNumber: '', specialty: '' };

        return {
          // Primary identifiers
          prescription_id: prescription._id || prescription.prescriptionId || prescription.id,
          
          // Prescription information
          medication_name: prescription.medication?.nombre || '',
          active_ingredient: prescription.medication?.principioActivo || '',
          national_code: prescription.medication?.codigoNacional || '',
          laboratory: prescription.medication?.laboratorio || '',
          status: prescription.status || 'UNKNOWN',
          
          // Dosage and treatment
          posology: prescription.posology || {},
          treatment_duration: prescription.treatmentDuration || {},
          number_of_packages: prescription.numberOfPackages || 1,
          special_instructions: prescription.specialInstructions || '',
          
          // Dates
          created_date: prescription.createdAt || prescription.created_date,
          expiration_date: prescription.dates?.expiration || prescription.expirationDate || prescription.validUntil,
          
          // Personnel
          prescribed_by: prescriberInfo.name,
          prescribed_by_id: prescriberInfo.id,
          prescribed_by_collegiate: prescriberInfo.collegiateNumber,
          prescribed_by_specialty: prescriberInfo.specialty,
          
          // Patient info
          patient_id: patientInfo.id,
          patient_name: patientInfo.name,
          
          // Preserve original for detailed view
          _original: prescription
        };
      });

      console.log('Normalized prescriptions:', normalizedPrescriptions);
      
      // **IMPORTANTE: Solo actualizar si el contador cambió**
      if (prescriptions.length !== normalizedPrescriptions.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${prescriptions.length} a ${normalizedPrescriptions.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedPrescriptions.length);
      }
      
      setPrescriptions(normalizedPrescriptions);
    } catch (error) {
      console.error('Error loading prescriptions:', error);
      setPrescriptions([]);
      // También actualizar contador en caso de error
      if (prescriptions.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, prescriptions.length, onCountUpdate, componentName, patientData]);

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadPrescriptionsStats = useCallback(async () => {
    if (!patientId || prescriptions.length === 0) return;
    
    try {
      const expiringCount = prescriptions.filter(prescription => {
        const expirationDate = prescription.expiration_date;
        if (!expirationDate) return false;
        
        const daysUntilExpiration = electronicPrescriptionService.getDaysUntilExpiration(expirationDate);
        return daysUntilExpiration !== null && daysUntilExpiration <= 7 && daysUntilExpiration >= 0;
      }).length;
      
      const activeCount = prescriptions.filter(prescription => 
        prescription.status?.toUpperCase() === 'EMITIDA' || prescription.status?.toUpperCase() === 'ACTIVE'
      ).length;
      
      if (expiringCount > 0 || activeCount > 0) {
        setAlertInfo({
          alert_type: 'prescription',
          message: `${activeCount} prescriptions active${expiringCount > 0 ? `, ${expiringCount} expiring soon` : ''}`,
          priority: expiringCount > 0 ? 'high' : 'medium',
          requires_action: expiringCount > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading prescriptions stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, prescriptions]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Prescriptions array length:', prescriptions.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...prescriptions];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(prescription => 
        (prescription.medication_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (prescription.active_ingredient || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (prescription.laboratory || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (prescription.prescribed_by || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Aplicar filtros específicos
    if (filters.status !== 'All') {
      filtered = filtered.filter(prescription => 
        prescription.status?.toUpperCase() === filters.status?.toUpperCase()
      );
    }

    if (filters.medicationType) {
      filtered = filtered.filter(prescription => 
        (prescription.active_ingredient || '').toLowerCase().includes(filters.medicationType.toLowerCase())
      );
    }

    if (filters.prescribedBy) {
      filtered = filtered.filter(prescription => 
        (prescription.prescribed_by || '').toLowerCase().includes(filters.prescribedBy.toLowerCase())
      );
    }

    if (filters.createdDate) {
      filtered = filtered.filter(prescription => {
        const createdDate = prescription.created_date;
        return createdDate && new Date(createdDate) >= new Date(filters.createdDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(prescription => {
        const createdDate = prescription.created_date;
        return createdDate && new Date(createdDate) <= new Date(filters.endDate);
      });
    }

    if (filters.expirationStatus !== 'All') {
      filtered = filtered.filter(prescription => {
        const expirationDate = prescription.expiration_date;
        if (!expirationDate) return filters.expirationStatus === 'no-expiration';
        
        const daysUntilExpiration = electronicPrescriptionService.getDaysUntilExpiration(expirationDate);
        
        switch (filters.expirationStatus) {
          case 'expired':
            return daysUntilExpiration < 0;
          case 'expiring-soon':
            return daysUntilExpiration >= 0 && daysUntilExpiration <= 7;
          case 'valid':
            return daysUntilExpiration > 7;
          default:
            return true;
        }
      });
    }

    console.log('Filtered array length:', filtered.length);

    // Mejorar la función de ordenamiento con validaciones
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.created_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'status': {
          const statusOrder = { 'EMITIDA': 3, 'BORRADOR': 2, 'ANULADA': 1 };
          const statusA = (a.status || '').toUpperCase();
          const statusB = (b.status || '').toUpperCase();
          return (statusOrder[statusB] || 0) - (statusOrder[statusA] || 0);
        }
        case 'alphabetical':
          const medicationA = (a.medication_name || '').toLowerCase();
          const medicationB = (b.medication_name || '').toLowerCase();
          return medicationA.localeCompare(medicationB);
        case 'expiration':
          const expA = a.expiration_date ? new Date(a.expiration_date) : new Date('9999-12-31');
          const expB = b.expiration_date ? new Date(b.expiration_date) : new Date('9999-12-31');
          return expA - expB;
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredPrescriptions(filtered);
  }, [prescriptions, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'status': return 'By Status';
      case 'alphabetical': return 'A-Z';
      case 'expiration': return 'By Expiration';
      default: return 'Sort by';
    }
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'medication_name',
      header: 'Medication',
      size: 250,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.medication_name || 'Unknown Medication'}
            </div>
            <small className="text-muted">
              {row.original.national_code && `Code: ${row.original.national_code}`}
            </small>
            <br />
            <small className="text-info">
              {formatDate(row.original.created_date)}
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'active_ingredient',
      header: 'Active Ingredient',
      size: 180,
      Cell: ({ row }) => (
        <div>
          <div className="fw-medium">{row.original.active_ingredient || 'Not specified'}</div>
          {row.original.laboratory && (
            <small className="text-muted">{row.original.laboratory}</small>
          )}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)} d-flex align-items-center gap-1`}>
          {getStatusIcon(row.original.status)}
          <span className="text-capitalize">
            {electronicPrescriptionService.getReadableStatus(row.original.status)}
          </span>
        </span>
      )
    },
    {
      accessorKey: 'posology',
      header: 'Posology',
      size: 150,
      Cell: ({ row }) => (
        <div>
          <div className="text-wrap">
            {row.original.posology?.cantidad || 'Not specified'}
          </div>
          {row.original.posology?.frecuencia && (
            <small className="text-muted">{row.original.posology.frecuencia}</small>
          )}
        </div>
      )
    },
    {
      accessorKey: 'treatment_duration',
      header: 'Duration',
      size: 120,
      Cell: ({ row }) => (
        <div>
          {row.original.treatment_duration?.value && row.original.treatment_duration?.unit ? (
            <span className="badge bg-info">
              {row.original.treatment_duration.value} {row.original.treatment_duration.unit}
            </span>
          ) : (
            <span className="text-muted">Not specified</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'expiration_date',
      header: 'Expiration',
      size: 150,
      Cell: ({ row }) => {
        const expirationDate = row.original.expiration_date;
        if (!expirationDate) return <span className="text-muted">No expiration</span>;
        
        const daysUntilExpiration = electronicPrescriptionService.getDaysUntilExpiration(expirationDate);
        const isExpired = daysUntilExpiration < 0;
        const isExpiringSoon = daysUntilExpiration >= 0 && daysUntilExpiration <= 7;
        
        return (
          <div>
            <div className={isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : ''}>
              {formatDate(expirationDate)}
            </div>
            {daysUntilExpiration !== null && (
              <small className={`${isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : 'text-muted'}`}>
                {isExpired 
                  ? `Expired ${Math.abs(daysUntilExpiration)} days ago`
                  : daysUntilExpiration === 0
                  ? 'Expires today'
                  : `${daysUntilExpiration} days left`
                }
              </small>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'prescribed_by',
      header: 'Prescribed By',
      size: 150,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.prescribed_by || 'Unknown'}</div>
          {row.original.prescribed_by_specialty && (
            <small className="text-muted">{row.original.prescribed_by_specialty}</small>
          )}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPrescription(row.original.prescription_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-outline-success btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleGeneratePDF(row.original.prescription_id);
            }}
            title="Generate PDF"
          >
            <FiFileText />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleCancelPrescription(row.original.prescription_id);
            }}
            title="Cancel Prescription"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ], [prescriptions]);

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadPrescriptions();
    }
  }, [patientId, loadPrescriptions]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (prescriptions.length > 0) {
      loadPrescriptionsStats();
    }
  }, [prescriptions, loadPrescriptionsStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${prescriptions.length}`);
    onCountUpdate && onCountUpdate(componentName, prescriptions.length);
  }, [prescriptions.length, onCountUpdate, componentName]);

  // Apply filters whenever prescriptions or filters change
  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- prescriptions.length:', prescriptions.length);
    console.log('- sortBy:', sortBy);
    
    if (prescriptions.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay prescriptions, limpiando filteredPrescriptions');
      setFilteredPrescriptions([]);
    }
  }, [prescriptions, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
  if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showPDFModal) {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '17px';
  } else {
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
  }
  
  return () => {
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
  };
}, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showPDFModal]);

  // Solo mostrar loading si estamos esperando datos específicos
  if (isLoading && patientId) {
    return <div>Loading patient prescriptions...</div>;
  }

  const handleViewPrescription = (prescriptionId) => {
    console.log('Viewing prescription:', prescriptionId);
    const prescription = prescriptions.find(p => p.prescription_id === prescriptionId);
    if (prescription) {
      console.log('Found prescription for viewing:', prescription);
      setSelectedPrescription(prescription);
      setShowViewOffcanvas(true);
    } else {
      console.error('Prescription not found:', prescriptionId);
    }
  };

  const handleGeneratePDF = (prescriptionId) => {
  console.log('Generating PDF for prescription:', prescriptionId);
  const prescription = prescriptions.find(p => p.prescription_id === prescriptionId);
  if (prescription) {
    console.log('Found prescription for PDF generation:', prescription);
    setSelectedPrescriptionForPDF(prescription);
    setShowPDFModal(true);
  } else {
    console.error('Prescription not found for PDF:', prescriptionId);
    alert('Error: No se pudo encontrar la prescripción para generar el PDF');
  }
};

  const handleCancelPrescription = async (prescriptionId) => {
    if (window.confirm('Are you sure you want to cancel this prescription?')) {
      try {
        await electronicPrescriptionService.cancelPrescription(prescriptionId);
        await loadPrescriptions();
        await loadPrescriptionsStats();
      } catch (error) {
        console.error('Error canceling prescription:', error);
        alert('Error canceling prescription. Please try again.');
      }
    }
  };

  const handleAddNew = () => {
  console.log('Add New button clicked - redirecting to prescription creation');
    
  // Prepare patient data in the same format as received from patient list
  const patientDataForNavigation = {
    patientId: patientData?.patientId || patientId,
    patientName: patientData?.patientName,
    patientDob: patientData?.patientDob,
    patientEmail: patientData?.patientEmail,
    patientPhone: patientData?.patientPhone
  };
  
  console.log('Navigating with patient data:', patientDataForNavigation);
  
  // Navigate to electronic prescription with patient data
  navigate('/electronic-prescription', {
    state: patientDataForNavigation
  });
};

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'All',
      medicationType: '',
      prescribedBy: '',
      createdDate: '',
      endDate: '',
      expirationStatus: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedPrescription(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  const handleOpenFilterOffcanvas = () => {
    // Cerrar cualquier offcanvas abierto antes de abrir el de filtros
    setShowAddOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'EMITIDA':
      case 'ACTIVE':
        return 'success';
      case 'BORRADOR':
      case 'DRAFT':
        return 'warning';
      case 'ANULADA':
      case 'CANCELLED':
        return 'danger';
      case 'DISPENSADA':
      case 'DISPENSED':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'EMITIDA':
      case 'ACTIVE':
        return <FiCheck />;
      case 'BORRADOR':
      case 'DRAFT':
        return <FiEdit3 />;
      case 'ANULADA':
      case 'CANCELLED':
        return <FiX />;
      case 'DISPENSADA':
      case 'DISPENSED':
        return <FiFileText />;
      default:
        return <FiInfo />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.medicationType) {
      activeFilters.push({
        key: 'medicationType',
        label: `Medication: ${filters.medicationType}`,
        value: filters.medicationType,
        type: 'medicationType'
      });
    }
    
    if (filters.prescribedBy) {
      activeFilters.push({
        key: 'prescribedBy',
        label: `Prescribed by: ${filters.prescribedBy}`,
        value: filters.prescribedBy,
        type: 'prescribedBy'
      });
    }
    
    if (filters.createdDate) {
      activeFilters.push({
        key: 'createdDate',
        label: `From: ${formatDate(filters.createdDate)}`,
        value: filters.createdDate,
        type: 'createdDate'
      });
    }
    
    if (filters.endDate) {
      activeFilters.push({
        key: 'endDate',
        label: `To: ${formatDate(filters.endDate)}`,
        value: filters.endDate,
        type: 'endDate'
      });
    }

    if (filters.expirationStatus !== 'All') {
      activeFilters.push({
        key: 'expirationStatus',
        label: `Expiration: ${filters.expirationStatus}`,
        value: filters.expirationStatus,
        type: 'expirationStatus'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: 'All' }));
        break;
      case 'medicationType':
        setFilters(prev => ({ ...prev, medicationType: '' }));
        break;
      case 'prescribedBy':
        setFilters(prev => ({ ...prev, prescribedBy: '' }));
        break;
      case 'createdDate':
        setFilters(prev => ({ ...prev, createdDate: '' }));
        break;
      case 'endDate':
        setFilters(prev => ({ ...prev, endDate: '' }));
        break;
      case 'expirationStatus':
        setFilters(prev => ({ ...prev, expirationStatus: 'All' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      status: 'All',
      medicationType: '',
      prescribedBy: '',
      createdDate: '',
      endDate: '',
      expirationStatus: 'All'
    });
    setSortBy('newest');
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/my-prescriptions'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Electronic Prescriptions {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search prescriptions..."
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'status' ? 'active' : ''}`}
                          onClick={() => handleSortChange('status')}
                        >
                          By Status
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'expiration' ? 'active' : ''}`}
                          onClick={() => handleSortChange('expiration')}
                        >
                          By Expiration
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleOpenFilterOffcanvas}
                  >
                    <FiFilter />
                    Filter
                  </button>
            
                  {/* Add New Button */}
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={handleAddNew}
                  >
                    <FiPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Table */}
          <MaterialReactTable
            columns={columns}
            data={filteredPrescriptions}
            
            // Configuración básica
            enableRowSelection
            enableColumnFilters
            enableGlobalFiltering
            enableSorting={false}
            enablePagination
            
            // Toolbars
            enableTopToolbar
            enableBottomToolbar
            
            // Features opcionales
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableColumnOrdering
            enableColumnResizing={false}
            enableGrouping={false}
            
            // Estados
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: 'medication_name', desc: false }],
              density: 'comfortable',
              showGlobalFilter: false
            }}
            
            state={{
              isLoading,
              rowSelection,
              globalFilter
            }}
            
            // Callbacks
            onRowSelectionChange={setRowSelection}
            onGlobalFilterChange={handleGlobalFilterChange}
            
            // Configuración de renderizado
            muiTableContainerProps={{
              sx: { maxHeight: '600px' }
            }}
          />

          {/* Results Summary */}
          {filteredPrescriptions.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredPrescriptions.length} of {prescriptions.length} prescriptions
                {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Electronic Prescriptions</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseFilterOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="EMITIDA">Active (Emitida)</option>
                    <option value="BORRADOR">Draft (Borrador)</option>
                    <option value="ANULADA">Cancelled (Anulada)</option>
                    <option value="DISPENSADA">Dispensed (Dispensada)</option>
                  </select>
                </div>

                {/* Medication Type Filter */}
                <div className="mb-3">
                  <label className="form-label">Medication/Active Ingredient</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by medication or active ingredient"
                    value={filters.medicationType}
                    onChange={(e) => handleFilterChange('medicationType', e.target.value)}
                  />
                </div>

                {/* Prescribed By Filter */}
                <div className="mb-3">
                  <label className="form-label">Prescribed By</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by doctor name"
                    value={filters.prescribedBy}
                    onChange={(e) => handleFilterChange('prescribedBy', e.target.value)}
                  />
                </div>

                {/* Expiration Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Expiration Status</label>
                  <select
                    className="form-select"
                    value={filters.expirationStatus}
                    onChange={(e) => handleFilterChange('expirationStatus', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="expired">Expired</option>
                    <option value="expiring-soon">Expiring Soon ({"≤"} 7 days)</option>
                    <option value="valid">Valid ({">"} 7 days)</option>
                    <option value="no-expiration">No Expiration Date</option>
                  </select>
                </div>

                {/* Date Range Filters */}
                <div className="mb-3">
                  <label className="form-label">Created From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.createdDate}
                    onChange={(e) => handleFilterChange('createdDate', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Created To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Prescription Offcanvas */}
      {showViewOffcanvas && selectedPrescription && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Prescription Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-3">
                {/* Basic Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Prescription Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Prescription ID</label>
                          <p className="form-control-plaintext font-mono">
                            {selectedPrescription.prescription_id || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getStatusColor(selectedPrescription.status)} d-flex align-items-center gap-1`} style={{width: 'fit-content'}}>
                              {getStatusIcon(selectedPrescription.status)}
                              {electronicPrescriptionService.getReadableStatus(selectedPrescription.status)}
                            </span>
                          </p>
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-bold">Medication</label>
                          <p className="form-control-plaintext">
                            <strong>{selectedPrescription.medication_name || 'Not specified'}</strong>
                            {selectedPrescription.national_code && (
                              <><br /><small className="text-muted">Code: {selectedPrescription.national_code}</small></>
                            )}
                            {selectedPrescription.laboratory && (
                              <><br /><small className="text-info">{selectedPrescription.laboratory}</small></>
                            )}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Active Ingredient</label>
                          <p className="form-control-plaintext">
                            {selectedPrescription.active_ingredient || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Number of Packages</label>
                          <p className="form-control-plaintext">
                            {selectedPrescription.number_of_packages || 1}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal para generar PDF */}
{showPDFModal && selectedPrescriptionForPDF && (
  <ElectronicPrescriptionPDF
    prescriptionData={selectedPrescriptionForPDF._original || selectedPrescriptionForPDF}
    selectedPatient={{
      _id: selectedPrescriptionForPDF.patient_id,
      name: selectedPrescriptionForPDF.patient_name?.split(' ')[0] || '',
      surname: selectedPrescriptionForPDF.patient_name?.split(' ').slice(1).join(' ') || '',
      dateOfBirth: patientData?.patientDob,
      idNumber: patientData?.patientId,
      email: patientData?.patientEmail,
      phone: patientData?.patientPhone
    }}
    selectedDoctor={{
      _id: selectedPrescriptionForPDF.prescribed_by_id,
      name: selectedPrescriptionForPDF.prescribed_by?.split(' ')[0] || '',
      surname: selectedPrescriptionForPDF.prescribed_by?.split(' ').slice(1).join(' ') || '',
      collegiateNumber: selectedPrescriptionForPDF.prescribed_by_collegiate,
      specialty: selectedPrescriptionForPDF.prescribed_by_specialty
    }}
    medicationDetails={{
      nombre: selectedPrescriptionForPDF.medication_name,
      principioActivo: selectedPrescriptionForPDF.active_ingredient,
      codigoNacional: selectedPrescriptionForPDF.national_code,
      laboratorio: selectedPrescriptionForPDF.laboratory
    }}
    prescriptionConfig={{
      posology: selectedPrescriptionForPDF.posology,
      treatmentDuration: selectedPrescriptionForPDF.treatment_duration,
      numberOfPackages: selectedPrescriptionForPDF.number_of_packages,
      specialInstructions: selectedPrescriptionForPDF.special_instructions
    }}
    onClose={() => {
      setShowPDFModal(false);
      setSelectedPrescriptionForPDF(null);
    }}
  />
)}

                {/* Treatment Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Treatment Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Posology</label>
                          <p className="form-control-plaintext">
                            {selectedPrescription.posology?.cantidad || 'Not specified'}
                            {selectedPrescription.posology?.frecuencia && (
                              <><br /><small className="text-muted">{selectedPrescription.posology.frecuencia}</small></>
                            )}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Treatment Duration</label>
                          <p className="form-control-plaintext">
                            {selectedPrescription.treatment_duration?.value && selectedPrescription.treatment_duration?.unit ? 
                              `${selectedPrescription.treatment_duration.value} ${selectedPrescription.treatment_duration.unit}` : 
                              'Not specified'
                            }
                          </p>
                        </div>
                        {selectedPrescription.special_instructions && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Special Instructions</label>
                            <p className="form-control-plaintext">
                              {selectedPrescription.special_instructions}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Date Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiCalendar className="me-1" />
                            Created On
                          </label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedPrescription.created_date)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiCalendar className="me-1" />
                            Expiration Date
                          </label>
                          <p className="form-control-plaintext">
                            {selectedPrescription.expiration_date ? (
                              <>
                                {formatDate(selectedPrescription.expiration_date)}
                                {(() => {
                                  const daysUntilExpiration = electronicPrescriptionService.getDaysUntilExpiration(selectedPrescription.expiration_date);
                                  if (daysUntilExpiration !== null) {
                                    const isExpired = daysUntilExpiration < 0;
                                    const isExpiringSoon = daysUntilExpiration >= 0 && daysUntilExpiration <= 7;
                                    return (
                                      <><br /><small className={`${isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : 'text-muted'}`}>
                                        {isExpired 
                                          ? `Expired ${Math.abs(daysUntilExpiration)} days ago`
                                          : daysUntilExpiration === 0
                                          ? 'Expires today'
                                          : `${daysUntilExpiration} days remaining`
                                        }
                                      </small></>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            ) : (
                              'No expiration date'
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personnel Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Personnel Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiUser className="me-1" />
                            Prescribed By
                          </label>
                          <p className="form-control-plaintext">
                            {selectedPrescription.prescribed_by || 'Unknown'}
                            {selectedPrescription.prescribed_by_collegiate && (
                              <><br /><small className="text-muted">Collegiate: {selectedPrescription.prescribed_by_collegiate}</small></>
                            )}
                            {selectedPrescription.prescribed_by_specialty && (
                              <><br /><small className="text-info">{selectedPrescription.prescribed_by_specialty}</small></>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-outline-success flex-fill"
                      onClick={() => handleGeneratePDF(selectedPrescription.prescription_id)}
                    >
                      <FiFileText className="me-2" />
                      Generate PDF
                    </button>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

export default ElectronicPrescriptionsTreatment;